import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './NewAmazonPaymentData.css';

const NewAmazonPaymentData = () => {
  const [existingDataRange, setExistingDataRange] = useState(null);
  const [availableReports, setAvailableReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingReports, setIsCheckingReports] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [downloadSummary, setDownloadSummary] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingReports, setDownloadingReports] = useState(new Set());

  // Load existing data range on component mount
  useEffect(() => {
    loadExistingDataRange();
  }, []);

  const loadExistingDataRange = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiCall('/api/new-amazon-payments/data-range', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExistingDataRange(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load existing data range');
      }
    } catch (error) {
      console.error('Error loading existing data range:', error);
      setError('Error loading existing data range. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkAvailableReports = async () => {
    try {
      setIsCheckingReports(true);
      setError(null);
      setAvailableReports([]);
      
      const response = await apiCall('/api/new-amazon-payments/available-reports', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableReports(data.reports || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to check available reports');
      }
    } catch (error) {
      console.error('Error checking available reports:', error);
      setError('Error checking available reports. Please check the console for details.');
    } finally {
      setIsCheckingReports(false);
    }
  };

  const downloadReport = async (reportId, reportDocumentId, dataStartTime, dataEndTime) => {
    try {
      setDownloadingReports(prev => new Set(prev).add(reportId));
      setError(null);
      setDownloadProgress('🚀 Starting download...');
      setDownloadSummary(null);
      
      const response = await apiCall('/api/new-amazon-payments/download-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          report_id: reportId,
          report_document_id: reportDocumentId,
          data_start_time: dataStartTime,
          data_end_time: dataEndTime
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDownloadSummary(data);
        setDownloadProgress('✅ Download completed successfully!');
        
        // Refresh the existing data range to show updated information
        await loadExistingDataRange();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to download report');
        setDownloadProgress('❌ Download failed');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      setError('Error downloading report. Please check the console for details.');
      setDownloadProgress('❌ Download failed');
    } finally {
      setDownloadingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="new-amazon-payment-data">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Amazon Payment Data Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Manages Amazon settlement reports using the new SP-API</li>
              <li>Downloads and processes settlement data automatically</li>
              <li>Shows summary statistics for order transactions</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Existing Data Range Section */}
        <div className="section">
          <h2>📊 Existing Data in Database</h2>
          {isLoading ? (
            <div className="loading">Loading existing data range...</div>
          ) : error ? (
            <div className="error">❌ {error}</div>
          ) : existingDataRange ? (
            <div className="data-range-info">
              <div className="range-item">
                <strong>Earliest Date:</strong> {formatDate(existingDataRange.earliest_date)}
              </div>
              <div className="range-item">
                <strong>Latest Date:</strong> {formatDate(existingDataRange.latest_date)}
              </div>
              <div className="range-item">
                <strong>Total Records:</strong> {existingDataRange.total_records?.toLocaleString() || '0'}
              </div>
              <div className="range-item">
                <strong>Total Order Principal:</strong> {formatCurrency(existingDataRange.total_order_principal)}
              </div>
            </div>
          ) : (
            <div className="no-data">No payment data found in database</div>
          )}
        </div>

        {/* Available Reports Section */}
        <div className="section reports-section">
          <div className="section-header">
            <h2>🔍 Available Settlement Reports</h2>
            <button 
              onClick={checkAvailableReports}
              disabled={isCheckingReports}
              className="check-reports-btn"
            >
              {isCheckingReports ? 'Checking...' : 'Check Available Reports'}
            </button>
          </div>
          
          {isCheckingReports && (
            <div className="loading">Checking for available settlement reports...</div>
          )}
          
          {availableReports.length > 0 && (
            <div className="reports-list">
              <h3>Found {availableReports.length} available reports:</h3>
              {availableReports
                .filter(report => {
                  // Filter out reports that aren't exactly 14 days
                  const reportStart = new Date(report.dataStartTime);
                  const reportEnd = new Date(report.dataEndTime);
                  const daysDiff = (reportEnd - reportStart) / (24 * 60 * 60 * 1000);
                  return Math.abs(daysDiff - 14) < 0.1; // Allow small tolerance for time precision
                })
                .map((report, index) => (
                <div key={report.reportId} className="report-item">
                  <div className="report-header">
                    <h4>Report {index + 1}: {report.reportId}</h4>
                  </div>
                  <div className="report-details">
                    <div className="detail-row">
                      <strong>Data Period:</strong> {formatDate(report.dataStartTime)} to {formatDate(report.dataEndTime)}
                    </div>
                    <div className="detail-row">
                      <strong>Created:</strong> {formatDate(report.createdTime)}
                    </div>
                    <div className="detail-row">
                      <strong>Marketplaces:</strong> {report.marketplaceIds?.length || 0} marketplaces
                    </div>
                  </div>
                  {report.processingStatus === 'DONE' && (
                    <button
                      onClick={() => downloadReport(
                        report.reportId,
                        report.reportDocumentId,
                        report.dataStartTime,
                        report.dataEndTime
                      )}
                      disabled={downloadingReports.has(report.reportId)}
                      className="download-btn"
                    >
                      {downloadingReports.has(report.reportId) ? 'Downloading...' : 'Download & Process'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {availableReports.length === 0 && !isCheckingReports && (
            <div className="no-reports">
              No available reports found. Click "Check Available Reports" to search for new settlement data.
            </div>
          )}
        </div>

        {/* Download Progress */}
        {downloadProgress && (
          <div className="section">
            <h2>📥 Download Progress</h2>
            <div className="progress-message">{downloadProgress}</div>
          </div>
        )}

        {/* Download Summary */}
        {downloadSummary && (
          <div className="section">
            <h2>✅ Download Summary</h2>
            <div className="summary-info">
              <div className="summary-item">
                <strong>Records Inserted:</strong> {downloadSummary.records_inserted?.toLocaleString() || '0'}
              </div>
              <div className="summary-item">
                <strong>Records Skipped (Duplicates):</strong> {downloadSummary.records_skipped?.toLocaleString() || '0'}
              </div>
              <div className="summary-item">
                <strong>Total Order Principal Added:</strong> {formatCurrency(downloadSummary.total_order_principal)}
              </div>
              <div className="summary-item">
                <strong>Processing Time:</strong> {downloadSummary.processing_time || 'N/A'}
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="section">
            <h2>❌ Error</h2>
            <div className="error-message">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewAmazonPaymentData; 